// SPDX-License-Identifier: GPL-3.0-or-later
// Key rebinding (THEME.md section 11.1: all keys can be changed in the options menu).
// The bindings live in local storage. A bad or missing value gives the defaults (fail closed).
// Rules: one key serves one command. A new key replaces the old keys of its command.
// A key that another command needs is refused, so no command is ever left without a key.
// This module has no DOM. The page binds it to the settings dialog.

import { KEY_MAP } from "./keys.ts";

/** The commands that have keys. */
export const BINDABLE_COMMANDS = [
  "left_flipper",
  "right_flipper",
  "plunger",
  "nudge_up",
  "start",
  "pause",
] as const;

export type BindableCommand = (typeof BINDABLE_COMMANDS)[number];

/** The key codes of each command. */
export type Bindings = Readonly<Record<BindableCommand, readonly string[]>>;

/** The part of local storage that this module uses. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** Key of the saved bindings in local storage. */
export const BINDINGS_KEY = "pinball.keys";

/** Most keys that one command may have. */
const MAX_KEYS_PER_COMMAND = 8;
/** A key code has this shape (for example `KeyZ`, `ShiftLeft`, `Digit1`). */
const CODE_SHAPE = /^[A-Za-z][A-Za-z0-9]{0,23}$/;
/** Keys that no command may use: they break menus or belong to the system. */
const RESERVED_CODES: ReadonlySet<string> = new Set([
  "Tab",
  "MetaLeft",
  "MetaRight",
  "OSLeft",
  "OSRight",
  "ContextMenu",
  "Unidentified",
]);

/** The name of each command, as the settings dialog shows it. */
export const COMMAND_LABELS: Readonly<Record<BindableCommand, string>> = {
  left_flipper: "Left flipper",
  right_flipper: "Right flipper",
  plunger: "Plunger",
  nudge_up: "Nudge",
  start: "Start game",
  pause: "Pause",
};

function isBindable(command: string): command is BindableCommand {
  return (BINDABLE_COMMANDS as readonly string[]).includes(command);
}

/** The default bindings of THEME.md section 11.1. */
export function defaultBindings(): Bindings {
  const out: Record<BindableCommand, string[]> = {
    left_flipper: [],
    right_flipper: [],
    plunger: [],
    nudge_up: [],
    start: [],
    pause: [],
  };
  for (const [code, command] of Object.entries(KEY_MAP)) {
    if (isBindable(command)) out[command].push(code);
  }
  return out;
}

/** True when a code can be bound to a command. */
export function isBindableCode(code: string, command: BindableCommand): boolean {
  if (!CODE_SHAPE.test(code) || RESERVED_CODES.has(code)) return false;
  // Escape cancels the capture in the settings dialog, so only Pause may keep it.
  return code !== "Escape" || command === "pause";
}

/** A lookup from key code to command. A code that is not bound is missing. */
export function bindingsToMap(bindings: Bindings): ReadonlyMap<string, BindableCommand> {
  const map = new Map<string, BindableCommand>();
  for (const command of BINDABLE_COMMANDS) {
    for (const code of bindings[command]) map.set(code, command);
  }
  return map;
}

/** The result of a rebind. */
export type RebindResult =
  | { readonly ok: true; readonly bindings: Bindings }
  | { readonly ok: false; readonly message: string };

/** Bind one key to a command. The key replaces the old keys of that command. */
export function rebind(bindings: Bindings, command: BindableCommand, code: string): RebindResult {
  if (!isBindableCode(code, command)) {
    return { ok: false, message: "That key cannot be used. Press another key." };
  }
  const next: Record<BindableCommand, string[]> = {
    left_flipper: [...bindings.left_flipper],
    right_flipper: [...bindings.right_flipper],
    plunger: [...bindings.plunger],
    nudge_up: [...bindings.nudge_up],
    start: [...bindings.start],
    pause: [...bindings.pause],
  };
  for (const other of BINDABLE_COMMANDS) {
    if (other === command || !next[other].includes(code)) continue;
    const rest = next[other].filter((c) => c !== code);
    if (rest.length === 0) {
      return {
        ok: false,
        message: `${keyLabel(code)} is the only key of "${COMMAND_LABELS[other]}". Change that command first.`,
      };
    }
    next[other] = rest;
  }
  next[command] = [code];
  return { ok: true, bindings: next };
}

/** Read the saved bindings. Anything that is not valid gives the defaults. */
export function loadBindings(storage: StorageLike | null): Bindings {
  const record = readSaved(storage);
  return (record === null ? null : bindingsFromRecord(record)) ?? defaultBindings();
}

/** The saved value as a plain object. A missing or bad value gives null. */
function readSaved(storage: StorageLike | null): Record<string, unknown> | null {
  if (storage === null) return null;
  try {
    const text = storage.getItem(BINDINGS_KEY);
    if (text === null) return null;
    const value: unknown = JSON.parse(text);
    const plain = typeof value === "object" && value !== null && !Array.isArray(value);
    return plain ? (value as Record<string, unknown>) : null;
  } catch {
    // Blocked storage or a text that is not JSON: the defaults apply.
    return null;
  }
}

/** The codes of one command in a saved record. A code that is not valid or used twice gives null. */
function savedCodes(
  record: Record<string, unknown>,
  command: BindableCommand,
  seen: Set<string>,
): string[] | null {
  const list = Object.hasOwn(record, command) ? record[command] : undefined;
  if (!Array.isArray(list) || list.length === 0 || list.length > MAX_KEYS_PER_COMMAND) return null;
  const codes: string[] = [];
  for (const code of list) {
    if (typeof code !== "string" || !isBindableCode(code, command) || seen.has(code)) return null;
    seen.add(code);
    codes.push(code);
  }
  return codes;
}

/** The bindings of a saved record. One bad command gives null: all or nothing. */
function bindingsFromRecord(record: Record<string, unknown>): Bindings | null {
  const out = defaultBindings() as Record<BindableCommand, readonly string[]>;
  const seen = new Set<string>();
  for (const command of BINDABLE_COMMANDS) {
    const codes = savedCodes(record, command, seen);
    if (codes === null) return null;
    out[command] = codes;
  }
  return out;
}

/** Save the bindings. A blocked storage keeps them for this page only. */
export function saveBindings(storage: StorageLike | null, bindings: Bindings): void {
  try {
    storage?.setItem(BINDINGS_KEY, JSON.stringify(bindings));
  } catch {
    // The choice stays for this page only.
  }
}

/** Forget the saved bindings. The result is the defaults. */
export function resetBindings(storage: StorageLike | null): Bindings {
  try {
    storage?.removeItem(BINDINGS_KEY);
  } catch {
    // Nothing to remove.
  }
  return defaultBindings();
}

const SPECIAL_LABELS: Readonly<Record<string, string>> = {
  ShiftLeft: "Left Shift",
  ShiftRight: "Right Shift",
  ControlLeft: "Left Ctrl",
  ControlRight: "Right Ctrl",
  AltLeft: "Left Alt",
  AltRight: "Right Alt",
  ArrowLeft: "Left Arrow",
  ArrowRight: "Right Arrow",
  ArrowUp: "Up Arrow",
  ArrowDown: "Down Arrow",
  Slash: "/",
  Backslash: "\\",
  Comma: ",",
  Period: ".",
  Semicolon: ";",
  Quote: "'",
  BracketLeft: "[",
  BracketRight: "]",
  Minus: "-",
  Equal: "=",
  Backquote: "`",
  NumpadEnter: "Numpad Enter",
};

/** A name for a key code, for people. */
export function keyLabel(code: string): string {
  const special = Object.hasOwn(SPECIAL_LABELS, code) ? SPECIAL_LABELS[code] : undefined;
  if (special !== undefined) return special;
  const letter = /^Key([A-Z])$/.exec(code);
  if (letter !== null) return letter[1] ?? code;
  const digit = /^Digit(\d)$/.exec(code);
  if (digit !== null) return digit[1] ?? code;
  const pad = /^Numpad(\d)$/.exec(code);
  if (pad !== null) return `Numpad ${pad[1]}`;
  return code;
}

/** The keys of one command as text. */
export function keysText(bindings: Bindings, command: BindableCommand): string {
  return bindings[command].map(keyLabel).join(", ");
}

/** One line for the start screen. */
export function keysSummary(bindings: Bindings): string {
  return `Keys: ${keysText(bindings, "left_flipper")} for the left flipper. ${keysText(bindings, "right_flipper")} for the right flipper. Hold ${keysText(bindings, "plunger")} to charge the plunger and release to launch. ${keysText(bindings, "nudge_up")} nudges. ${keysText(bindings, "pause")} pauses.`;
}
